import React, { useEffect, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { Provider } from 'react-redux';
import { useFonts } from 'expo-font';
import { store, loadPersistedState } from './src/store';
import { RootNavigator } from './src/navigation/RootNavigator';
import { colors } from './src/theme/colors';
import { SupportNotificationsProvider, useSupportNotifications } from './src/services/support-notifications';
import { capturePredispatchDeepLink } from './src/services/predispatch-deeplink';
import { Toast } from './src/components/Toast';

/** Global support toast — fires when a new inbound SUPPORT message arrives. */
function GlobalSupportToast() {
  const { toast, clearToast } = useSupportNotifications();
  return (
    <Toast
      visible={!!toast}
      message={toast?.message || ''}
      type="info"
      duration={3000}
      onDismiss={clearToast}
    />
  );
}

function AppContent() {
  const [ready, setReady] = useState(false);

  // Restore persisted auth token, driver, certifications, active jobs from AsyncStorage
  // MUST complete before rendering navigator so token is available
  useEffect(() => {
    // Capture the ?predispatch=<token> deep link (web) before auth resolves; the
    // token is persisted and consumed (resolve + navigate) by RootNavigator once a
    // real auth token is present. No-op when PREDISPATCH_ENABLED is false.
    capturePredispatchDeepLink().catch(() => {});
    loadPersistedState().finally(() => setReady(true));
  }, []);

  const [fontsLoaded] = useFonts({
    'Roobert-Regular':   require('./assets/fonts/Roobert-Regular.ttf'),
    'Roobert-Medium':    require('./assets/fonts/Roobert-Medium.ttf'),
    'Roobert-SemiBold':  require('./assets/fonts/Roobert-SemiBold.ttf'),
    'Roobert-Heavy':     require('./assets/fonts/Roobert-Heavy.ttf'),
    'Reckless-Regular':  require('./assets/fonts/RecklessStandardM-Regular.ttf'),
    'Reckless-SemiBold': require('./assets/fonts/RecklessStandardM-SemiBold.ttf'),
    'Reckless-Bold':     require('./assets/fonts/RecklessStandardM-Bold.ttf'),
  });

  if (!fontsLoaded || !ready) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.sand, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator color={colors.black} size="large" />
      </View>
    );
  }

  return (
    <Provider store={store}>
      <SupportNotificationsProvider>
        <RootNavigator />
        <GlobalSupportToast />
      </SupportNotificationsProvider>
    </Provider>
  );
}

export default function App() {
  return <AppContent />;
}
